import { useState, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { avatarSrc, randomAvatarIds } from '../avatar/avatarList';
import './Auth.css';

const PICKER_SIZE = 6;

const EyeIcon = ({ visible, onClick }) => (
  <button type="button" className="password-toggle" onClick={onClick} tabIndex={-1} aria-label={visible ? 'Hide password' : 'Show password'}>
    {visible ? (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </svg>
    ) : (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
    )}
  </button>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getPasswordStrength = (password) => {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };
  const passed = Object.values(checks).filter(Boolean).length;
  let label, color;
  if (passed <= 2) { label = 'Weak'; color = '#ef4444'; }
  else if (passed <= 3) { label = 'Fair'; color = '#f59e0b'; }
  else if (passed <= 4) { label = 'Good'; color = '#3b82f6'; }
  else { label = 'Strong'; color = '#22c55e'; }
  return { checks, passed, label, color };
};

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [avatarChoices, setAvatarChoices] = useState(() => randomAvatarIds(PICKER_SIZE));
  const [selectedAvatar, setSelectedAvatar] = useState(() => avatarChoices[0]);
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const shuffleAvatars = useCallback(() => {
    const next = randomAvatarIds(PICKER_SIZE, [selectedAvatar]);
    setAvatarChoices([selectedAvatar, ...next].slice(0, PICKER_SIZE));
  }, [selectedAvatar]);

  const strength = useMemo(() => getPasswordStrength(password), [password]);
  const emailValid = !email || EMAIL_RE.test(email);
  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;
  const passwordsMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!EMAIL_RE.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (strength.passed < 4) {
      setError('Password is too weak. Include uppercase, lowercase, number, and special character.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password, selectedAvatar);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page auth-page--split">
      {/* Background */}
      <div className="auth-bg">
        <img
          src="https://images.unsplash.com/photo-1558799401-1dcba79834c2?w=1920&q=80"
          alt=""
          aria-hidden="true"
        />
        <div className="auth-bg-overlay" />
      </div>

      <div className="auth-container">
        {/* Brand Panel */}
        <aside className="auth-brand-panel" aria-hidden="true">
          <div className="auth-brand-grid" />
          <div className="auth-brand-glow" />

          <div className="auth-brand-top">
            <div className="auth-brand-logo">
              <img src="/logo/logo.png" alt="MapHunt logo" />
              <span className="auth-brand-name">MapHunt</span>
            </div>
          </div>

          <div className="auth-brand-body">
            <h2 className="auth-brand-tagline">
              Discover Nepal,<br /><span className="accent">one quest at a time.</span>
            </h2>
            <p className="auth-brand-sub">
              Join thousands of explorers hunting real locations across the country.
            </p>

            <ul className="auth-brand-features">
              <li>
                <span className="feature-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                </span>
                <div>
                  <strong>Real Nepal landmarks</strong>
                  <span>Hunt quests across 12+ cities and trails</span>
                </div>
              </li>
              <li>
                <span className="feature-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                  </svg>
                </span>
                <div>
                  <strong>Live multiplayer</strong>
                  <span>Race friends in real-time hunts</span>
                </div>
              </li>
              <li>
                <span className="feature-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
                    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                    <path d="M4 22h16"/>
                    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
                    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
                    <path d="M18 2H6v7a6 6 0 0 0 12 0V2z"/>
                  </svg>
                </span>
                <div>
                  <strong>Climb the leaderboard</strong>
                  <span>Earn ranks, badges, and bragging rights</span>
                </div>
              </li>
            </ul>
          </div>

          <div className="auth-brand-footer">
            <div className="auth-brand-stats">
              <div>
                <span className="stat-num">200+</span>
                <span className="stat-label">Quests</span>
              </div>
              <div>
                <span className="stat-num">12</span>
                <span className="stat-label">Cities</span>
              </div>
              <div>
                <span className="stat-num">5k+</span>
                <span className="stat-label">Hunters</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Form Card */}
        <div className="auth-card">
          <div className="auth-header">
            <h1>Create your account</h1>
            <p>Start hunting in under a minute.</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="auth-form" noValidate>
            {error && (
              <div className="alert alert-error" role="alert" aria-live="polite">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span>{error}</span>
              </div>
            )}

            <Input
              label="Full Name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              required
              autoComplete="name"
            />

            <Input
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              placeholder="you@example.com"
              required
              autoComplete="email"
              error={emailTouched && !emailValid ? 'Enter a valid email address' : undefined}
            />

            <div className="avatar-picker">
              <div className="avatar-picker-head">
                <label className="avatar-picker-label">Pick your avatar</label>
                <button
                  type="button"
                  className="avatar-shuffle"
                  onClick={shuffleAvatars}
                  aria-label="Shuffle avatar options"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="16 3 21 3 21 8"/>
                    <line x1="4" y1="20" x2="21" y2="3"/>
                    <polyline points="21 16 21 21 16 21"/>
                    <line x1="15" y1="15" x2="21" y2="21"/>
                    <line x1="4" y1="4" x2="9" y2="9"/>
                  </svg>
                  Shuffle
                </button>
              </div>
              <div className="avatar-grid" role="radiogroup" aria-label="Choose an avatar">
                {avatarChoices.map((id) => {
                  const isSelected = id === selectedAvatar;
                  return (
                    <button
                      key={id}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      className={`avatar-option${isSelected ? ' is-selected' : ''}`}
                      onClick={() => setSelectedAvatar(id)}
                    >
                      <span className="avatar-option-inner">
                        <img src={avatarSrc(id)} alt="" draggable={false} />
                      </span>
                      {isSelected && (
                        <span className="avatar-check" aria-hidden="true">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="input-row">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 8 characters"
                required
                autoComplete="new-password"
                iconRight={<EyeIcon visible={showPassword} onClick={() => setShowPassword(v => !v)} />}
              />
              <Input
                label="Confirm Password"
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat password"
                required
                autoComplete="new-password"
                iconRight={<EyeIcon visible={showConfirm} onClick={() => setShowConfirm(v => !v)} />}
                error={passwordsMismatch ? "Passwords don't match" : undefined}
              />
            </div>

            {password && (
              <div className="field-group">
                <div className="password-strength">
                  <div className="strength-bar" aria-hidden="true">
                    {[1, 2, 3, 4, 5].map(i => (
                      <div
                        key={i}
                        className="strength-segment"
                        style={i <= strength.passed ? { background: strength.color } : undefined}
                      />
                    ))}
                  </div>
                  <span className="strength-label" style={{ color: strength.color }}>{strength.label}</span>
                </div>
                <ul className="strength-checks" aria-label="Password requirements">
                  <li className={strength.checks.length ? 'met' : ''}>8+ characters</li>
                  <li className={strength.checks.uppercase ? 'met' : ''}>Uppercase</li>
                  <li className={strength.checks.lowercase ? 'met' : ''}>Lowercase</li>
                  <li className={strength.checks.number ? 'met' : ''}>Number</li>
                  <li className={strength.checks.special ? 'met' : ''}>Special char</li>
                </ul>
              </div>
            )}

            {passwordsMatch && (
              <div className="match-hint" aria-live="polite">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Passwords match
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              block
            >
              Create Account
            </Button>

            <p className="auth-terms">
              By creating an account you agree to our <a href="/terms">Terms</a> and <a href="/privacy">Privacy Policy</a>.
            </p>
          </form>

          <div className="auth-footer">
            <p>
              Already have an account? <Link to="/login" state={location.state}>Sign in</Link>
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Register;
